import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Loader2, ChevronRight, ShoppingBag } from 'lucide-react';
import { BackButton } from '../../components/ui/BackButton';
import { SHOPPER_HOME } from '../../constants/routes';
import { useAuth } from '../../hooks/useAuth';
import { useOrders } from '../../hooks/useOrders';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

const TERMINAL_STATES = new Set(['CANCELLED', 'REFUNDED', 'DISPUTED', 'DELIVERED', 'COMPLETED']);

function formatState(state: string): string {
  return state.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

function formatMoney(order: { total?: number; payment?: { amount?: number } }): string {
  if (typeof order.total === 'number' && order.total > 0) {
    return `$${order.total.toFixed(2)}`;
  }
  if (typeof order.payment?.amount === 'number') {
    return `$${(order.payment.amount / 100).toFixed(2)}`;
  }
  return '$0.00';
}

function formatDate(value: unknown): string {
  if (!value) return '';
  const d = typeof (value as { toDate?: () => Date }).toDate === 'function'
    ? (value as { toDate: () => Date }).toDate()
    : value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function OrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { orders, loading, error } = useOrders(user?.uid || '', 'shopper');

  const active = orders.filter((o) => !TERMINAL_STATES.has(o.state));
  const past = orders.filter((o) => TERMINAL_STATES.has(o.state));

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <BackButton fallback={SHOPPER_HOME} />
            <h1 className="ml-4 text-xl font-serif text-heading">Your Orders</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-8 pb-24">
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 text-body">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
            <p className="text-sm">Loading orders…</p>
          </div>
        )}

        {!loading && error && (
          <Card>
            <CardContent className="p-6 text-center text-sm text-red-600">
              Could not load orders. Please try again.
            </CardContent>
          </Card>
        )}

        {!loading && !error && orders.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <div className="w-20 h-20 rounded-full bg-purple-50 flex items-center justify-center mb-4">
              <Package className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-xl font-serif text-heading mb-2">No orders yet</h2>
            <p className="text-body text-sm mb-6 max-w-xs">
              When you place an order, it will show up here so you can track delivery in real time.
            </p>
            <Button onClick={() => navigate(SHOPPER_HOME)}>
              <ShoppingBag className="h-4 w-4 mr-2" />
              Start Shopping
            </Button>
          </div>
        )}

        {!loading && !error && orders.length > 0 && (
          <>
            {active.length > 0 && (
              <section>
                <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
                  Active ({active.length})
                </h2>
                <div className="space-y-3">
                  {active.map((order) => (
                    <OrderRow key={order.id} order={order} active />
                  ))}
                </div>
              </section>
            )}

            {past.length > 0 && (
              <section>
                <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
                  Past orders
                </h2>
                <div className="space-y-3">
                  {past.map((order) => (
                    <OrderRow key={order.id} order={order} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function OrderRow({
  order,
  active = false,
}: {
  order: {
    id: string;
    state: string;
    businessName?: string;
    items?: Array<{ name?: string }>;
    total?: number;
    payment?: { amount?: number; status?: string };
    createdAt?: unknown;
  };
  active?: boolean;
}) {
  const itemPreview = (order.items || [])
    .slice(0, 2)
    .map((i) => i.name)
    .filter(Boolean)
    .join(', ');

  return (
    <Link to={`/orders/${order.id}`}>
      <Card className={cn('hover:border-primary/30 transition-colors', active && 'border-primary/20 bg-primary/5')}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-heading">
                  Order #{order.id.slice(-6).toUpperCase()}
                </p>
                {active && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                    Live
                  </span>
                )}
              </div>
              <p className="text-sm text-body truncate mt-0.5">
                {order.businessName || 'Boutique'}
                {itemPreview ? ` · ${itemPreview}` : ''}
              </p>
              <p className="text-xs text-body mt-1 capitalize">
                {formatState(order.state)}
                {order.createdAt ? ` · ${formatDate(order.createdAt)}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm font-semibold text-heading">{formatMoney(order)}</span>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
